/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState } from "react";
import DataTable from "react-data-table-component";
import { Helmet } from "react-helmet";
import { withRouter } from "react-router";
import { encuestaCategoryKpiIcons, statusEncuestaEnviada, statusEncuestaRespondida, totalKpiDef } from "../../../config/constants";
import { BlurSpinner } from "../../components/BlurSpinner";
import Breadcrumb from "../../components/common/breadcrumb";
import DateRangeComponent from "../../components/DateRange";
import KpiCard from "../../components/kpiCard";
import { StyledSelect } from "../../components/StyledSelect";
import { useAuth } from "../../context/AuthContext";
import useFetcher from "../../hooks/useFetcher";
import usePaging from "../../hooks/usePaging";
import { fetchCasasCentrales } from "../../services/casaCentralServices";
import { fetchEncuestaCategories, fetchEncuestas } from "../../services/encuestasServices";
import { fetchRegiones } from "../../services/regionServices";
import { fetchSucursales } from "../../services/sucursalServices";
import { retriveRegionSucursalOptions } from "../../utils/retriveRegionSucursalOptions";
import modulosId from "../../mocks/modulos";

const Encuestas = ({ history }) => {
  const { currentUser, haveSucursalesAccess } = useAuth();
  // eslint-disable-next-line max-len
  const [encuestaCategories, isLoadingEncuestaCategories, errEncuestaCategories] = useFetcher(fetchEncuestaCategories);
  const [regiones, isLoadingRegiones] = useFetcher(fetchRegiones);
  const [casasCentrales, isLoadingCasasCentrales] = useFetcher(fetchCasasCentrales);
  const [sucursales, isLoadingSucursales] = useFetcher(fetchSucursales);
  const pagingController = usePaging(fetchEncuestas);
  const [findQuery, setFindQuery] = useState(pagingController.query.query || "");
  const clasificacionStats = pagingController.statusStats && pagingController.statusStats.clasificaciones ? pagingController.statusStats.clasificaciones : [];
  const totalStats = clasificacionStats.reduce((total, stat) => total + parseInt(stat.count, 10), 0);
  const statusStats = pagingController.statusStats && pagingController.statusStats.status ? pagingController.statusStats.status : [];
  const totalEnviadas = statusStats.reduce((total, stat) => (stat.status === statusEncuestaEnviada ? total + parseInt(stat.count, 10) : total), 0);
  const totalRespondidas = statusStats.reduce((total, stat) => (stat.status === statusEncuestaRespondida ? total + parseInt(stat.count, 10) : total), 0);

  const helperRegionSucursal = retriveRegionSucursalOptions(
    pagingController.query.region,
    pagingController.query.sucursal,
    regiones,
    sucursales,
    casasCentrales,
    isLoadingRegiones || isLoadingCasasCentrales || isLoadingSucursales,
    haveSucursalesAccess(modulosId.ENCUESTA),
    currentUser.user
  );

  const tiposOptions = encuestaCategories.tipo
    ? encuestaCategories.tipo
        .map((tipo) => {
          return { label: tipo.text, value: tipo.id };
        })
        .sort((a, b) => (a.label > b.label ? 1 : -1))
    : [];

  let serviciosOptions = [];
  const serviceToOptions = (category, tipo) => {
    if (!pagingController.query.tipo || pagingController.query.tipo === tipo) {
      serviciosOptions = serviciosOptions.concat(
        ((category && category["servicio-2"]) || []).map((servicio) => ({
          label: servicio.text,
          value: servicio.text,
        }))
      );
    }
  };
  serviceToOptions(encuestaCategories.fromAsociados, "1");
  serviceToOptions(encuestaCategories.fromCandidatos, "2");
  serviceToOptions(encuestaCategories.fromClientes, "3");
  const existentIds = [];
  serviciosOptions = serviciosOptions
    .sort((a, b) => (a.label > b.label ? 1 : -1))
    // Filtro duplicados
    .filter((servicio) => {
      if (servicio === null || existentIds.includes(servicio.value)) {
        return false;
      }
      existentIds.push(servicio.value);
      return true;
    });

  const handleDatesChange = (values) => {
    pagingController.setFilterValue({ startDate: values.rangeDates.startDate, endDate: values.rangeDates.endDate });
  };

  const handleQueryChange = (event) => {
    setFindQuery(event.target.value);
  };

  const handleQueryBlur = (event) => {
    if (pagingController.query.query !== event.target.value) {
      pagingController.setFilterValue({ query: event.target.value });
    }
  };

  const handleQueryKeyDown = (event) => {
    if (event.key === "Enter" && pagingController.query.query !== event.target.value) {
      pagingController.setFilterValue({ query: event.target.value });
    }
  };

  const handleReactSelectChange = (selected, options) => {
    const newState = { [options.name]: selected ? selected.value : null };
    if (newState.region) {
      newState.sucursal = null;
    }
    if (newState.tipo) {
      newState.servicio = null;
    }
    pagingController.setFilterValue(newState);
  };

  const handleFilterStatus = (clasificacion) => {
    pagingController.setFilterValue({ clasificacion });
  };

  const handleEncuestaClicked = (row) => {
    history.push({
      pathname: `/encuestas/${row.itemId}/detail`,
      state: { encuesta: row },
    });
  };

  return (
    <>
      <Helmet>
        <title>PIN - Sistema de Encuestas</title>
      </Helmet>
      <Breadcrumb title="Encuestas" parent="Sistema de Encuestas" />
      <BlurSpinner
        isLoading={
          // eslint-disable-next-line prettier/prettier
          pagingController.isLoading || isLoadingCasasCentrales || isLoadingRegiones || isLoadingSucursales || isLoadingEncuestaCategories
        }
      >
        <div className="container-fluid">
          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-body">
                  <div className="row">
                    <div className="col-sm-3">
                      <div className="form-group">
                        <label htmlFor="region" className="col-form-label pt-0 w-100">
                          Region
                          <StyledSelect
                            id="region"
                            name="region"
                            className="w-100"
                            onChange={handleReactSelectChange}
                            value={
                              helperRegionSucursal.regionesOptions.find((option) => {
                                return option.value === helperRegionSucursal.selectedRegion;
                              }) || null
                            }
                            placeholder="Todo"
                            isClearable
                            isLoading={isLoadingRegiones}
                            isDisabled={isLoadingRegiones || helperRegionSucursal.disableRegionSucursal}
                            options={helperRegionSucursal.regionesOptions}
                          />
                        </label>
                      </div>
                    </div>
                    <div className="col-sm-3">
                      <div className="form-group">
                        <label htmlFor="sucursal" className="col-form-label pt-0 w-100">
                          Sucursal
                          <StyledSelect
                            id="sucursal"
                            name="sucursal"
                            className="w-100"
                            onChange={handleReactSelectChange}
                            value={
                              helperRegionSucursal.casasAndSucusOptions.find((option) => {
                                return option.value === helperRegionSucursal.selectedSucursal;
                              }) || null
                            }
                            placeholder="Todo"
                            isClearable
                            isLoading={isLoadingCasasCentrales || isLoadingSucursales}
                            isDisabled={isLoadingCasasCentrales || isLoadingSucursales || helperRegionSucursal.disableRegionSucursal}
                            options={helperRegionSucursal.casasAndSucusOptions}
                          />
                        </label>
                      </div>
                    </div>
                    <div className="col-sm-3">
                      <div className="form-group">
                        <label htmlFor="tipo" className="col-form-label pt-0 w-100">
                          Publico
                          <StyledSelect
                            id="tipo"
                            name="tipo"
                            className="w-100"
                            onChange={handleReactSelectChange}
                            value={tiposOptions.find((option) => {
                              return option.value === parseInt(pagingController.query.tipo, 10);
                            })}
                            placeholder="Todo"
                            isClearable
                            isLoading={isLoadingEncuestaCategories}
                            disabled={isLoadingEncuestaCategories}
                            options={tiposOptions}
                          />
                        </label>
                      </div>
                    </div>
                    <div className="col-sm-3">
                      <div className="form-group">
                        <label htmlFor="servicio" className="col-form-label pt-0 w-100">
                          Servicio
                          <StyledSelect
                            id="servicio"
                            name="servicio"
                            className="w-100"
                            onChange={handleReactSelectChange}
                            value={serviciosOptions.find((option) => option.value === pagingController.query.servicio) || null}
                            placeholder="Todo"
                            isClearable
                            isLoading={isLoadingEncuestaCategories}
                            disabled={isLoadingEncuestaCategories}
                            options={serviciosOptions}
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-sm-9 col-12">
                      <div className="form-group">
                        <label htmlFor="query" className="col-form-label pt-0 w-100">
                          Busqueda por texto
                          <input
                            className="form-control"
                            type="search"
                            placeholder="Buscar.."
                            onBlur={handleQueryBlur}
                            onChange={handleQueryChange}
                            onKeyDown={handleQueryKeyDown}
                            name="query"
                            id="query"
                            value={findQuery}
                          />
                        </label>
                      </div>
                    </div>
                    <div className="col-sm-3">
                      <div className="form-group">
                        <DateRangeComponent onChange={handleDatesChange} values={pagingController.query} label="Rango de fechas" />
                      </div>
                    </div>
                  </div>
                  {isLoadingEncuestaCategories || errEncuestaCategories ? (
                    <></>
                  ) : (
                    <div className="row">
                      <div className="col-xl-3 col-sm-6">
                        <KpiCard
                          statusDef={totalKpiDef}
                          value={{
                            count: totalStats,
                          }}
                          detail={
                            <div className="text-md-right">
                              <ul>
                                <li>
                                  Enviadas:
                                  <span className="product-stts font-info ml-2">{totalEnviadas}</span>
                                </li>
                                <li>
                                  Respondidas:
                                  <span className="product-stts font-info ml-2">{totalRespondidas}</span>
                                </li>
                              </ul>
                            </div>
                          }
                          total={totalStats}
                          onClick={handleFilterStatus}
                          selected={pagingController.query.clasificacion === undefined}
                        />
                      </div>
                      {encuestaCategories["clasificacion-2"].map((statusDef) => {
                        // eslint-disable-next-line max-len
                        const value = clasificacionStats.find((statusStat) => statusStat.clasificacion2 === statusDef.id);
                        return (
                          <div className="col-xl-3 col-sm-6" key={statusDef.id}>
                            <KpiCard
                              statusDef={statusDef}
                              icon={encuestaCategoryKpiIcons[statusDef.id]}
                              value={value}
                              total={totalStats}
                              onClick={handleFilterStatus}
                              selected={parseInt(pagingController.query.clasificacion, 10) === statusDef.id}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="table-responsive support-table">
                    <DataTable
                      title="Encuestas"
                      paginationComponentOptions={{ rowsPerPageText: "Encuestas por pagina", rangeSeparatorText: "De", selectAllRowsItemText: "Todos" }}
                      noDataComponent={
                        <div className="p-3">
                          <b>No se encontraron encuestas</b>
                        </div>
                      }
                      striped
                      pagination
                      sortServer
                      paginationServer
                      onRowClicked={handleEncuestaClicked}
                      data={pagingController.data}
                      onSort={pagingController.handleSortsChange}
                      paginationTotalRows={pagingController.totalRows}
                      paginationDefaultPage={parseInt(pagingController.query.page || 1, 10)}
                      onChangeRowsPerPage={pagingController.handleRecordsPerPageChange}
                      paginationPerPage={parseInt(pagingController.query.perpage || 10, 10)}
                      onChangePage={pagingController.handlePageChange}
                      columns={[
                        {
                          name: "Socio",
                          selector: (row) => {
                            if (row.RespondeAsociados) {
                              return `${row.RespondeAsociados.socio}`;
                            }
                            if (row.RespondeClientes) {
                              return `${row.RespondeClientes.socio}`;
                            }
                            if (row.RespondeCandidatos) {
                              return `${row.RespondeCandidatos.socio}`;
                            }
                            return "-";
                          },
                          sortable: true,
                          center: true,
                          id: "socio",
                          style: {
                            cursor: "pointer",
                            textAlign: "center",
                          },
                        },
                        {
                          name: "Identificador",
                          selector: (row) => {
                            if (row.RespondeAsociados) {
                              return parseInt(row.RespondeAsociados.cuit, 10);
                            }
                            if (row.RespondeClientes) {
                              return parseInt(row.RespondeClientes.cuit, 10);
                            }
                            if (row.RespondeCandidatos) {
                              return parseInt(row.RespondeCandidatos.cuit, 10);
                            }
                            return "-";
                          },
                          sortable: true,
                          center: true,
                          id: "cuit",
                          style: {
                            cursor: "pointer",
                            textAlign: "center",
                          },
                        },
                        {
                          name: "Servicio",
                          selector: (row) => {
                            let servicioCat = null;
                            if (row.RespondeAsociados) {
                              servicioCat = row.RespondeAsociados.servicio2Cat;
                            }
                            if (row.RespondeClientes) {
                              servicioCat = row.RespondeClientes.servicio2Cat;
                            }
                            if (row.RespondeCandidatos) {
                              servicioCat = row.RespondeCandidatos.servicio2Cat;
                            }
                            if (servicioCat) {
                              return (
                                <div className="text-center">
                                  <span
                                    className="badge"
                                    style={{
                                      fontSize: "unset",
                                      background: `#${servicioCat.color}`,
                                    }}
                                  >
                                    {servicioCat.text}
                                  </span>
                                </div>
                              );
                            }
                            return "-";
                          },
                          sortable: true,
                          center: true,
                          wrap: true,
                          id: "servicio",
                          style: {
                            cursor: "pointer",
                            textAlign: "center",
                          },
                        },
                        {
                          name: "Cliente",
                          selector: (row) => {
                            if (row.RespondeAsociados) {
                              return row.RespondeAsociados.cliente;
                            }
                            if (row.RespondeClientes) {
                              return row.RespondeClientes.razonSocial;
                            }
                            if (row.RespondeCandidatos) {
                              return row.RespondeCandidatos.cliente;
                            }
                            return "-";
                          },
                          sortable: true,
                          center: true,
                          wrap: true,
                          id: "cliente",
                          style: {
                            cursor: "pointer",
                            textAlign: "center",
                          },
                        },
                        {
                          name: "Sucursal",
                          selector: (row) => {
                            if (row.Sucursales) {
                              return row.Sucursales.titulo;
                            }
                            return "-";
                          },
                          sortable: true,
                          center: true,
                          wrap: true,
                          id: "sucursal",
                          style: {
                            cursor: "pointer",
                            textAlign: "center",
                          },
                        },
                        {
                          name: "Close Loop",
                          selector: ({ closeLoop, closeLoopCat }) =>
                            closeLoop ? (
                              <div className="text-center">
                                <span
                                  className="badge"
                                  style={{
                                    fontSize: "unset",
                                    background: `#${closeLoopCat.color}`,
                                  }}
                                >
                                  {closeLoopCat.text}
                                </span>
                              </div>
                            ) : (
                              <></>
                            ),
                          sortable: true,
                          center: true,
                          id: "closeLoop",
                          style: {
                            cursor: "pointer",
                            textAlign: "center",
                          },
                        },
                      ]}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </BlurSpinner>
    </>
  );
};

export default withRouter(Encuestas);
